import { useQuery } from '@tanstack/react-query'
import { get } from './client'
import type { ContextSnapshot } from '@/types/contextObservability'

export const contextKeys = {
  all: ['context-snapshots'] as const,
  list: (sessionId: string) =>
    ['context-snapshots', 'list', sessionId] as const,
  detail: (snapshotId: string) =>
    ['context-snapshots', 'detail', snapshotId] as const,
}

async function fetchContextSnapshots(sessionId: string): Promise<ContextSnapshot[]> {
  return get<ContextSnapshot[]>(`/sessions/${sessionId}/context-snapshots`)
}

async function fetchContextSnapshot(snapshotId: string): Promise<ContextSnapshot> {
  return get<ContextSnapshot>(`/context-snapshots/${snapshotId}`)
}

export function useContextSnapshots(sessionId: string) {
  return useQuery({
    queryKey: contextKeys.list(sessionId),
    queryFn: () => fetchContextSnapshots(sessionId),
    enabled: !!sessionId,
    staleTime: 30_000,
  })
}

export function useContextSnapshotDetail(snapshotId: string | null) {
  return useQuery({
    queryKey: contextKeys.detail(snapshotId ?? ''),
    queryFn: () => fetchContextSnapshot(snapshotId!),
    enabled: !!snapshotId,
    staleTime: 60_000,
  })
}

export async function downloadContextSnapshot(snapshotId: string): Promise<void> {
  const snapshot = await fetchContextSnapshot(snapshotId)
  const json = JSON.stringify(snapshot, null, 2)
  const blob = new Blob([json], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `context-snapshot-${snapshotId}.json`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}
